import { CoreContext } from 'context/CoreContext';
import { useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Icon } from 'ui/styled';
import { parseStrapiImage } from 'utils';
import {
  ArrowWrapper,
  DashboardMenu,
  DashboardMenuContainer,
  DashboardMenuContent,
  DashboardMenuFooter,
  DashboardMenuFooterContainer,
  DashboardMenuHeader,
  DashboardMenuOption,
  HeaderItemIconBadge,
  OptionsContainer,
  SideContent,
  SideOptionContent,
  SideUserItemEmail,
  SideUserItemImage,
  SideUserItemText,
  SideUserItemTitle,
  SubMenuItem,
} from './styled';

let persistedScrollY = 0;

export default function DashboardSide({ opened, setOpened }) {
  const history = useHistory();
  const navigate = (to) => history.push(`/${to}`);
  const pathname = history.location.pathname;
  const { content, reloadContent, user, openMenus, toggleMenu } =
    useContext(CoreContext);
  const menuScrollRef = useRef(null);

  const verifyClose = (e) => {
    if (!e.target.closest('.menu-contant')) {
      setOpened(false);
    }
  };

  const handleClick = async (item) => {
    if (item.subActions) {
      toggleMenu(item.name);
    } else if (item.page) {
      persistedScrollY = menuScrollRef.current?.scrollTop || 0;
      reloadContent(true);
      navigate(item.page);
    }
  };

  // O menu e remontado a cada troca de rota, entao a posicao do scroll e
  // reposta a partir de `persistedScrollY`. Isso acontece em useLayoutEffect
  // de proposito: ele roda antes da pintura, entao o menu ja aparece na
  // posicao certa. Antes era requestAnimationFrame — que roda DEPOIS da
  // pintura —, e para esconder o salto o menu ficava com opacity 0 e voltava
  // com transicao de 1.3s. Resultado: ele piscava a cada navegacao.
  useLayoutEffect(() => {
    if (menuScrollRef.current) {
      menuScrollRef.current.scrollTop = persistedScrollY;
    }
  }, [opened, pathname]);

  useEffect(() => reloadContent(), [reloadContent]);

  const menuItems = useMemo(() => {
    let items = [
      {
        name: 'Business Intelligence',
        page: 'home',
        icon: 'home-black',
        iconActive: 'home',
      },
      {
        name: 'Pré-Financiamento',
        icon: 'proposal-black',
        iconActive: 'proposal',
        subActions: [
          {
            name: 'Formulário',
            page: 'preproposal/financingpreproposal',
            isButton: true,
          },
          {
            name: 'Pré-Análise',
            page: 'preproposal/financingpreanalise',
            badge: content?.preAnalise,
          },
          {
            name: 'Recusadas',
            page: 'preproposal/preproposalrecusadas',
          },
          {
            name: 'Pré-Aprovado',
            page: 'preproposal/preproposalapproved',
            badge: content?.preAprovado,
          },
        ],
      },
      {
        name: 'Propostas',
        icon: 'proposal-black',
        iconActive: 'proposal',
        subActions: [
          {
            name: 'e-Proposta',
            page: 'eproposals',
            badge: content?.eProposta,
          },
          {
            name: 'Declinadas',
            page: 'declinadas',
          },
          {
            name: 'Elegíveis',
            page: 'proposals',
            badge: content?.pendentes,
          },
          {
            name: 'Inelegíveis',
            page: 'financing-table/recusadas',
          },
        ],
      },
      {
        name: 'Banking',
        icon: 'financing-table-black',
        iconActive: 'financing-table',
        subActions: [
          {
            name: 'Motor',
            page: 'banking/motor',
            badge: content?.bankingNegocio,
          },
          {
            name: 'Negócios',
            page: 'banking/negocio',
            badge: content?.bankingMesa,
          },
          {
            name: 'Crédito',
            page: 'banking/credito',
            badge: content?.bankingCredito,
          },
          {
            name: 'Formalização',
          },
          {
            name: 'Bancarização',
          },
        ],
      },
      {
        name: 'Cobrança',
        icon: 'proposal-black',
        iconActive: 'proposal',
        subActions: [
          {
            name: 'Carteira',
            page: 'billing/carteira',
            badge: content?.cobrancaCarteira,
          },
          // A Régua saiu do menu (a Cobrança assumiu o 1 a 30), mas a rota
          // billing/regua continua respondendo.
          //
          // Os badges da esteira usam chaves próprias do get-count, com o mesmo
          // filtro de cada tela (BILLING_QUERIES): cobranca1a30, cobranca31a60 e
          // cobranca61 (este COM os contratos em `renegociar`). As antigas
          // (cobrancaRegua, cobranca, cobrancaRecuperacao) contam as faixas
          // anteriores e não servem aqui. Sem a chave (backend antigo ou zero),
          // o badge some.
          {
            name: 'Cobrança',
            page: 'billing/cobranca',
            badge: content?.cobranca1a30,
          },
          {
            name: 'Recuperação',
            page: 'billing/recuperacao',
            badge: content?.cobranca31a60,
          },
          {
            name: 'Renegociação',
            page: 'billing/renegociacao',
            badge: content?.cobranca61,
          },
          {
            name: 'Notificação',
            page: 'billing/notificacao',
            badge: content?.cobrancaNotificacao,
          },
          {
            name: 'Execução',
            page: 'billing/execucao',
            badge: content?.cobrancaExecucao,
          },
          // Relatorios: sem badge, nao sao fila de trabalho.
          {
            name: 'Relatório de Baixas',
            page: 'billing/relatorios/baixas',
          },
          // Todos veem; sem a flag `desempenho_cobranca`, so os proprios numeros.
          {
            name: 'Desempenho',
            page: 'billing/relatorios/desempenho',
          },
        ],
      },
      {
        name: 'Financiamentos',
        icon: 'financing-table-black',
        iconActive: 'financing-table',
        subActions: [
          {
            name: 'Em Análise',
            page: 'financing-table/under-analysis',
            badge: content?.underAnalysis,
          },
          {
            name: 'Negadas',
            page: 'financing-table/negadas',
          },
          {
            name: 'Declinadas',
            page: 'financing-table/declinadas',
          },
          {
            name: 'Aprovadas',
            page: 'financing-table/approved',
            badge: content?.approved,
          },
          {
            name: 'A Autorizar',
            page: 'financing-table/authorized',
            badge: content?.authorized,
          },
          {
            name: 'A Formalizar',
            page: 'financing-table/formalizar',
            badge: content?.aFormalizar,
          },
          {
            name: 'A Pagar',
            page: 'financing-table/to-pay',
            badge: content?.toPay,
          },
        ],
      },
      {
        name: 'Protocolos',
        icon: 'table-control-black',
        iconActive: 'table-control',
        subActions: [
          {
            name: 'Expedição',
            page: 'table-control/expedicao',
            badge: content?.expedicao,
          },
          {
            name: 'Entrega e Parecer',
            page: 'table-control/delivery',
            badge: content?.entregaExpedicao,
          },
          {
            name: 'Instalação e Vistoria',
            page: 'table-control/installation',
            badge: content?.instalacao,
          },
          {
            name: 'Conexão e 1ª Parcela',
            page: 'table-control/connection',
            badge: content?.primeiraParcela,
          },
          {
            name: '1º Fatura com Injeção',
            page: 'table-control/first-invoice',
            badge: content?.injecaoEnergia,
          },
        ],
      },
      {
        name: 'Curadoria',
        icon: 'financing-table-black',
        iconActive: 'financing-table',
        subActions: [
          {
            name: 'Declinar proposta',
            page: 'menu_adm/declinarpropostas',
          },
          {
            name: 'Propostas',
            page: 'menu_adm/curadoriapropostas',
            badge: content?.curadoria,
          },
          {
            name: 'Negócios',
            page: 'menu_adm/curadorianegocios',
            badge: content?.curadoriaBanking,
          },
          {
            name: 'Financiamentos',
            page: 'menu_adm/curadoriafinanciamentos',
            badge: content?.financiamento,
          },
          {
            name: 'SOL | Financiamentos',
            page: 'menu_adm/solfinanciamentos',
            badge: content?.solFinanciamentos,
          },
          {
            name: 'SOL | Protocolos',
            page: 'menu_adm/solprotocolos',
            badge: content?.solProtocolos,
          },
          {
            name: 'Biblioteca',
            page: 'menu_adm/biblioteca',
          },
        ],
      },
    ];

    const canSeeCuradoria =
      user.administrador || user.banking_curadoria || user.biblioteca;
    //Cao não seja administrador remove o item de Curadoria
    if (!canSeeCuradoria) {
      items = items.filter((item) => item.name !== 'Curadoria');
    } else if (
      !user.administrador &&
      user.banking_curadoria &&
      user.biblioteca
    ) {
      //Se não for administrador mas for banking_curadoria
      items = items.map((item) => {
        if (item.name === 'Curadoria') {
          return {
            ...item,
            subActions: item.subActions.filter(
              (action) =>
                action.name === 'Negócios' || action.name === 'Biblioteca'
            ),
          };
        }
        return item;
      });
    } else if (
      !user.administrador &&
      !user.banking_curadoria &&
      user.biblioteca
    ) {
      //Se não for administrador mas for biblioteca
      items = items.map((item) => {
        if (item.name === 'Curadoria') {
          return {
            ...item,
            subActions: item.subActions.filter(
              (action) => action.name === 'Biblioteca'
            ),
          };
        }
        return item;
      });
    } else if (
      !user.administrador &&
      user.banking_curadoria &&
      !user.biblioteca
    ) {
      //Se não for administrador mas for biblioteca
      items = items.map((item) => {
        if (item.name === 'Curadoria') {
          return {
            ...item,
            subActions: item.subActions.filter(
              (action) => action.name === 'Negócios'
            ),
          };
        }
        return item;
      });
    }
    //Caso não tenha permissão do banking remove o item Banking
    if (!user.banking) {
      items = items.filter((item) => item.name !== 'Banking');
    }
    return items;
  }, [content, user]);

  const isItemActive = (item) => {
    const isFinancingDetail = /^\/proposals\/financing\/.+/.test(pathname);
    if (isFinancingDetail) {
      return false;
    }
    const hasActiveSubAction = item.subActions?.some(
      (sub) => sub.page && pathname.includes(sub.page)
    );
    const isMainPageActive = item.page && pathname.includes(item.page);
    return hasActiveSubAction || isMainPageActive;
  };

  return !opened ? null : (
    <DashboardMenuContainer onClick={verifyClose}>
      <DashboardMenu ref={menuScrollRef}>
        <DashboardMenuHeader>
          {process.env.REACT_APP_ENV === 'production' ? (
            <Icon icon={'logo'} />
          ) : (
            <Icon icon={'lodo-dev-com-tarja'} />
          )}
        </DashboardMenuHeader>
        <DashboardMenuContent>
          <SideContent>
            {menuItems.map((item, index) => (
              <DashboardMenuOption
                key={index}
                last={index === menuItems.length - 1}
              >
                <OptionsContainer
                  onClick={() => handleClick(item)}
                  active={isItemActive(item)}
                >
                  <SideOptionContent>
                    <Icon
                      icon={isItemActive(item) ? item.iconActive : item.icon}
                      nomargin
                    />
                    {item.name}
                  </SideOptionContent>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {!item?.badge ? null : (
                      <HeaderItemIconBadge>{item?.badge}</HeaderItemIconBadge>
                    )}
                    {item.subActions && (
                      <ArrowWrapper $isOpen={openMenus.includes(item.name)}>
                        <Icon
                          icon="chevron-down"
                          nomargin
                          style={{ width: '20px', height: '20px' }}
                        />
                      </ArrowWrapper>
                    )}
                  </div>
                </OptionsContainer>
                {item.subActions &&
                  openMenus.includes(item.name) &&
                  item.subActions.map((subItem, subIndex) => (
                    <SubMenuItem
                      key={subIndex}
                      onClick={() => {
                        if (subItem.page) {
                          persistedScrollY = menuScrollRef.current.scrollTop;
                          reloadContent(true);
                          navigate(subItem.page);
                        }
                      }}
                      active={
                        subItem.page === ''
                          ? false
                          : subItem.page === 'proposals'
                            ? pathname === '/proposals'
                            : pathname.includes(subItem.page)
                      }
                      $isButton={subItem.isButton}
                    >
                      {subItem.name}
                      {!subItem?.badge ? null : (
                        <HeaderItemIconBadge>
                          {subItem?.badge}
                        </HeaderItemIconBadge>
                      )}
                    </SubMenuItem>
                  ))}
              </DashboardMenuOption>
            ))}
          </SideContent>
        </DashboardMenuContent>
        <DashboardMenuFooterContainer>
          <DashboardMenuFooter
            onClick={() => window.open('mailto:contato@evontech.com.br')}
          >
            <Icon icon="email" nomargin />
            Fale Conosco
          </DashboardMenuFooter>
          <DashboardMenuFooter onClick={() => navigate('dashboard/me')}>
            <SideUserItemImage
              url={
                user?.image?.url
                  ? parseStrapiImage(user?.image?.url)
                  : '/images/no-user.png'
              }
              alt="user-image"
            />
            <SideUserItemTitle title={user?.nome}>
              {user?.nome}
              <SideUserItemText>
                {/* O title deixa o valor inteiro acessivel quando trunca. */}
                <SideUserItemEmail title={user?.username}>
                  {user?.username}
                </SideUserItemEmail>
                <Icon icon="arrow-right" />
              </SideUserItemText>
            </SideUserItemTitle>
          </DashboardMenuFooter>
        </DashboardMenuFooterContainer>
      </DashboardMenu>
    </DashboardMenuContainer>
  );
}
